// ===========================================
// Meets: en qué franja cae cada reunión (ORR-406)
// ===========================================

import { addMinutes, isSameDay, startOfDay } from 'date-fns'

import { inicioVisibleEvento, parseFechaEvento } from '@/lib/fechaEvento'

import type { FiltrosCalendario, ItemCalendario } from '@/core/types/calendario'
import type { EventoConOcurrencia } from '@/lib/fechaEvento'

/**
 * Lo que piden al calendario las tres vistas de meets. Sin esto la consulta baja además los
 * vencimientos y las tareas del mes entero, que ninguna de las tres mira.
 */
export const FILTROS_SOLO_MEETS: FiltrosCalendario = {
  incluir_vencimientos: false,
  incluir_tareas: false,
  tipos_evento: ['reunion'],
}

/**
 * Franja de un meet respecto del día de hoy. Es el semáforo que pidió el ticket:
 * `hoy` en verde, `manana` en amarillo, `proximo` en gris.
 */
export type FranjaMeet = 'hoy' | 'manana' | 'proximo' | 'pasado'

/** Duración que se asume cuando el evento no declara fin. */
const DURACION_ASUMIDA_MIN = 60

/**
 * Cuánto antes del inicio empieza a avisar.
 *
 * El aviso sirve si llega cerca de la hora. Dispararlo apenas empieza el día lo vuelve inútil:
 * quien lo cierra a la mañana se queda sin recordatorio justo cuando le haría falta.
 */
const ANTELACION_AVISO_MIN = 15

/** Campos de fin, con la misma regla de ocurrencia que el inicio (ORR-383). */
export interface MeetConFin extends EventoConOcurrencia {
  fecha_fin?: string | null
  ocurrencia_fin?: string | null
  estado?: string | null
}

/**
 * Estados que sacan a un meet de "lo que viene".
 *
 * El filtro vive acá y no en cada vista: una reunión cancelada abriendo el aviso bloqueante es
 * justo lo que entrena a cerrarlo sin leer, y alcanza con que UN consumidor se olvide.
 */
const ESTADOS_FUERA = ['cancelado', 'completado']

const vigente = (meet: MeetConFin) => !meet.estado || !ESTADOS_FUERA.includes(meet.estado)

/**
 * Fin visible de un meet. Cuando la fila viene de una serie, el fin de ESA ocurrencia es
 * `ocurrencia_fin`, que puede ser null aunque `ocurrencia_inicio` exista: en ese caso el fin
 * del maestro corresponde a otra semana y no sirve, así que se asume la duración.
 */
const finVisible = (meet: MeetConFin): Date | null => {
  const crudo = meet.ocurrencia_inicio !== undefined ? meet.ocurrencia_fin : meet.fecha_fin
  if (!crudo) return null
  const d = parseFechaEvento(crudo)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Fin efectivo de un meet: el declarado, o `DURACION_ASUMIDA_MIN` después del inicio.
 *
 * Un fin que no es posterior al inicio vale como ausente. El formulario de eventos acepta la
 * hora de fin sin compararla contra la de inicio, y ese rango invertido daría por terminada una
 * reunión que todavía no empezó.
 */
const finEfectivo = (meet: MeetConFin, inicio: Date): Date => {
  const declarado = finVisible(meet)
  if (declarado && declarado.getTime() > inicio.getTime()) return declarado
  return addMinutes(inicio, DURACION_ASUMIDA_MIN)
}

/** Un meet de hoy cuyo fin efectivo ya pasó. */
const yaTermino = (meet: MeetConFin, inicio: Date, ahora: Date): boolean =>
  finEfectivo(meet, inicio).getTime() < ahora.getTime()

/** Inicio visible ya parseado, o `null` si la fecha no es válida. */
export const inicioMeet = (meet: EventoConOcurrencia): Date | null => {
  try {
    const d = parseFechaEvento(inicioVisibleEvento(meet))
    return Number.isNaN(d.getTime()) ? null : d
  } catch {
    return null
  }
}

/**
 * En qué franja cae un meet. Compara DÍAS, no instantes: un meet de las 23:59 de hoy sigue
 * siendo `hoy`, y uno de las 00:01 de mañana ya es `manana`.
 *
 * Devuelve `null` si la fecha del evento es inválida — quien consume decide si lo oculta.
 */
export const franjaMeet = (meet: EventoConOcurrencia, ahora: Date): FranjaMeet | null => {
  const inicio = inicioMeet(meet)
  if (!inicio) return null

  const dia = startOfDay(inicio)
  const hoy = startOfDay(ahora)
  if (dia.getTime() < hoy.getTime()) return 'pasado'
  if (isSameDay(dia, hoy)) return 'hoy'

  const manana = startOfDay(addMinutes(hoy, 24 * 60))
  return isSameDay(dia, manana) ? 'manana' : 'proximo'
}

/**
 * Un meet que está por empezar o que está transcurriendo ahora.
 *
 * Es lo que decide si el aviso se abre, y cubre los dos casos que importan: faltan pocos minutos,
 * o ya arrancó y quien entra al sistema todavía llega. Queda afuera lo que ya terminó —a las
 * 18:00 nadie necesita que le bloqueen la pantalla por una reunión de las 09:00— y lo que es para
 * más tarde, que se ve en la tarjeta de Inicio sin interrumpir a nadie.
 *
 * Sin fin declarado se asumen `DURACION_ASUMIDA_MIN` minutos.
 */
export const meetInminente = (meet: MeetConFin, ahora: Date): boolean => {
  if (!vigente(meet)) return false
  if (franjaMeet(meet, ahora) !== 'hoy') return false
  const inicio = inicioMeet(meet)
  if (!inicio) return false
  if (yaTermino(meet, inicio, ahora)) return false

  return inicio.getTime() - ahora.getTime() <= ANTELACION_AVISO_MIN * 60_000
}

/**
 * Minutos que faltan para que empiece, o `0` si ya arrancó.
 *
 * Sirve para que el aviso diga cuánto falta en vez de un "hoy" que no ayuda a decidir nada.
 */
export const minutosParaEmpezar = (meet: MeetConFin, ahora: Date): number => {
  const inicio = inicioMeet(meet)
  if (!inicio) return 0
  return Math.max(0, Math.ceil((inicio.getTime() - ahora.getTime()) / 60_000))
}

/** Un meet con su franja y su inicio ya resueltos, listo para pintar. */
export interface MeetClasificado<T> {
  meet: T
  franja: FranjaMeet
  inicio: Date
}

/**
 * Los meets que valen la pena mostrar: de hoy en adelante, dentro del horizonte, ordenados.
 *
 * Deja afuera los pasados, los de fecha inválida y los de hoy cuyo fin efectivo ya pasó —
 * `franjaMeet` compara días, no instantes, así que una reunión de las 10 sigue siendo "hoy" a las
 * 18. El horizonte se cuenta en días completos desde hoy, igual que el semáforo, así que un meet
 * del último día entra aunque sea a la noche.
 */
export const clasificarProximos = <T extends MeetConFin>(
  meets: T[],
  ahora: Date,
  diasHorizonte: number,
): MeetClasificado<T>[] => {
  const limite = startOfDay(addMinutes(startOfDay(ahora), diasHorizonte * 24 * 60))

  return meets
    .reduce<MeetClasificado<T>[]>((acc, meet) => {
      if (!vigente(meet)) return acc
      const franja = franjaMeet(meet, ahora)
      const inicio = inicioMeet(meet)
      if (!franja || !inicio || franja === 'pasado') return acc
      if (franja === 'hoy' && yaTermino(meet, inicio, ahora)) return acc
      if (startOfDay(inicio).getTime() > limite.getTime()) return acc
      acc.push({ meet, franja, inicio })
      return acc
    }, [])
    .sort((a, b) => a.inicio.getTime() - b.inicio.getTime())
}

/** El usuario contra el que se decide si una reunión le corresponde. */
export interface UsuarioMeet {
  email: string | null
  esOwner: boolean
}

const normalizarEmail = (email: string) => email.trim().toLowerCase()

/**
 * Si la reunión le corresponde al usuario.
 *
 * Le corresponde al owner de la organización, a quien la creó y a quien figura en
 * `participantes`. Es la regla de `notificar_reuniones_proximas()`. Los emails se comparan sin
 * espacios y en minúsculas. Sin email, solo el owner ve la reunión.
 */
export const esMeetDelUsuario = (
  meet: Pick<ItemCalendario, 'participantes' | 'datos_originales'>,
  usuario: UsuarioMeet,
): boolean => {
  if (usuario.esOwner) return true
  if (!usuario.email) return false
  const propio = normalizarEmail(usuario.email)
  const creador = meet.datos_originales.created_by
  if (typeof creador === 'string' && normalizarEmail(creador) === propio) return true
  return (meet.participantes ?? []).some((p) => normalizarEmail(p) === propio)
}
